import styled from "styled-components";
import { tokens } from "../../../../../public/tokens";
import { MOBILE_BREAKPOINT } from "../../../../library/breakpoints";

/*
 * grid at gap-x-24px - left "Version Settings" spans columns 1-7, right settings card spans
 * 8-12 (StyledColumn below sets the explicit gridColumn spans), not an even 1fr/1fr split.
 */
export const StyledLayout = styled.div(({ $reserveFooterSpace = false }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
    gap: tokens.globals["spacing-6"],
    /*
     * "stretch" (grid's default, but stated explicitly since a previous "start" here
     * was the cause of a real bug) - both columns fill the row's full height, so the
     * shorter Statement of Results/Student Portal card's panel stretches to the same
     * bottom edge as the taller Version Settings card, instead of leaving a blank strip
     * beneath it. Each StyledPanel below is `height: 100%` so it actually fills that
     * stretched column rather than staying content-sized inside it.
     */
    alignItems: "stretch",
    marginTop: tokens.globals["spacing-6"],
    /* Sticky footer height + breathing room, so the bar never covers the last panel - only reserved while it's actually shown. */
    paddingBottom: $reserveFooterSpace ? tokens.globals["spacing-20"] : 0,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        gridTemplateColumns: "1fr",
    },
}));

export const StyledColumn = styled.div(({ $span }) => ({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-6"],
    height: "100%",
    gridColumn: $span ? `span ${$span}` : undefined,
}));

/*
 * The right card stacks two title+body sections (Statement of Results, Student Portal)
 * separated by a divider - that section-to-section gap (and the gap either side of the
 * divider) is 20px, while a title sits only 12px above its own fields. StyledPanel carries
 * the outer 20px rhythm; StyledPanelSection carries a title's own 12px gap to its body.
 */
export const StyledPanel = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-5"],
    height: "100%",
    boxSizing: "border-box",
    padding: tokens.globals["spacing-6"],
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
});

export const StyledPanelSection = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-3"],
    width: "100%",
});

export const StyledPanelBody = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-5"],
    width: "100%",
});

export const StyledPanelTitle = styled.h2({
    margin: 0,
    color: tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["xl-18px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.bold,
});

export const StyledToggleGrid = styled.div({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.globals["spacing-5"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        gridTemplateColumns: "1fr",
    },
});

/*
 * Student Portal switches sit at a wider gap than the SOR switches' 20px - no token
 * matches 30px exactly, so it's a one-off literal.
 */
export const StyledStudentToggleGrid = styled(StyledToggleGrid)({
    gap: "30px",
});

export const StyledSectionDivider = styled.hr({
    margin: 0,
    width: "100%",
    border: "none",
    borderTop: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
});

export const StyledFooterMessage = styled.span({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
});
